import { useUser, useSalesOrganization, useAuth, useCompany } from '../contexts/AuthContext';
import { usePackChrome } from '../contexts/PackChromeContext';
import PackPage from './layouts/so/PackPage';
import Scope1Dashboard from '../pages/so/Scope1Dashboard';
import { hasScope1Surface, scope1PageTitle } from '../utils/scope1Dashboard';

/**
 * @description SO operator dashboard
 *
 * @dependencies AuthContext, PackChromeContext, PackPage, Scope1Dashboard
 *
 * @notes
 * Pack SO: Scope 1 layout (LYN-392) — company or All of this sales org.
 * Title is {Company|SO name} Business Insights. No PackPage subtitle.
 * First-run modal lives on PackOnboardingHost. Click path is the SO door,
 * not admin.{cp}. Pack column uses PackPage (LYN-363) — no max-w-3xl shell.
 * All with no created companies is empty state, not an error. Pack chrome only.
 * Assets & Crew is Overview's sibling on this page — not a main rail item.
 */
const DASHBOARD_SUBNAV = [
  { key: 'overview', label: 'Overview', to: '/so/dashboard' },
  { key: 'assets-crew', label: 'Assets & Crew', to: '/so/assets-crew' }
];

function SODashboard() {
  const { user: currentUser, isLoaded } = useUser();
  const { salesOrganization } = useSalesOrganization();
  const { companyScope, activeCompanyId, companies } = useAuth();
  const { company } = useCompany();
  const { towtechSoChrome, loading: packLoading } = usePackChrome();

  if (!isLoaded || packLoading) {
    return <div>Loading...</div>;
  }

  if (towtechSoChrome) {
    const showTiles = hasScope1Surface(companyScope, activeCompanyId, companies);
    const title = scope1PageTitle({
      view: companyScope === 'all' ? 'all' : 'company',
      companyName: company?.businessName
        || companies.find((row) => row.id === (company?.id || activeCompanyId))?.businessName,
      salesOrgName: salesOrganization?.organizationName
    });
    return (
      <PackPage title={title} items={DASHBOARD_SUBNAV}>
        {showTiles ? (
          <Scope1Dashboard
            view={companyScope === 'all' ? 'all' : 'company'}
            companies={companies}
            company={company}
            country={salesOrganization?.country || null}
          />
        ) : (
          <p className="text-sm text-gray-500" data-testid="scope1-empty-company">
            No jobs to show. Add a company to see operations.
          </p>
        )}
      </PackPage>
    );
  }

  const displayName = salesOrganization?.organizationName
    ? salesOrganization.organizationName
    : currentUser?.firstName && currentUser?.lastName
    ? `${currentUser.firstName} ${currentUser.lastName.charAt(0)}.`
    : currentUser?.email || 'User';

  return (
    <div>
      <h1 className="text-3xl font-bold mb-6">Sales Organization Dashboard</h1>
      <p>Welcome, {displayName}!</p>
      <p>Role: {currentUser?.role || 'Unknown'}</p>
      <p>This dashboard will display SO-specific metrics and functionality (coming soon).</p>
    </div>
  );
}

export default SODashboard;
